/**
 * Roteirização multi-destino.
 *
 * A tela continua trabalhando sobre um único destino "ativo": os campos vivos do componente
 * (`destination`, `stops`, `routes`, parâmetros dos cards…) são sempre os do destino da página
 * atual. Os demais destinos ficam guardados em contextos e trocar de página = capturar o ativo e
 * restaurar o escolhido. A troca é por referência (sem cópia profunda): depois do restore os
 * campos vivos apontam para os objetos do novo contexto, e os do antigo ficam só no contexto dele.
 *
 * A atribuição das paradas aos destinos é feita pela distância em linha reta (haversine): a
 * parada vai para o destino mais próximo cujo raio máximo ela respeita; só fica "fora do raio"
 * quando viola o raio de todos os destinos. Com a alocação pelo destino definido (switch "por
 * unidade mais próxima" desligado), a parada que tem unidade (`id_unidade_destino`) vai só para o
 * destino daquela unidade e, na simulação, a que tem cerca (`id_cerca_destino`, coluna `cod_cerca`
 * do CSV) vai só para o destino daquela cerca; a cerca prevalece sobre a unidade.
 */
import { haversineMeters } from './haversine.js';

/**
 * Campos de `data` que pertencem a um destino. `invalidStops` (fora do raio de todos) e
 * `invalidProcessingStops` (sem geocodificação) ficam fora: são globais da sessão multi-destino.
 */
export const DESTINATION_CONTEXT_FIELDS = Object.freeze([
    // Destino e parâmetros da roteirização
    'destination',
    'selectedDirection',
    'maxTripDuration',
    'maxNumberofStops',
    'isClusteringEnabled',
    'serviceTime',
    'stopServiceMode',
    'stopServiceFixedSec',
    'stopServicePerPassengerSec',
    'alightServiceMode',
    'alightServiceFixedSec',
    'alightServicePerPassengerSec',
    'radiusConfig',
    'centerRadius',
    'previewSchedule',
    'previewArea',
    'operationDays',
    'operationTaxes',
    'minSimilarity',
    'initialBase',
    'initialBaseStop',
    'capacity',
    'tempoReusoVeiculos',
    // Frota
    'selectedFleetMode',
    'routingCompanyId',
    'routingClientId',
    'operationalVehicleTypeRows',
    'selectedOperationalVehicleTypes',
    'operationalContractsRows',
    'selectedContracts',
    'manualFleetEntries',
    'selectedAvailableVehicleType',
    'simulationFleetCosts',
    'operationalFleetPercentOverrides',
    // Turnos, raios e motoristas
    'destinationShifts',
    'destinationShiftsFonte',
    'driverParams',
    'driverCyclesSource',
    // Paradas do destino
    'stops',
    'originalCsvPoints',
    'csvImportActive',
    'csvImportPersonIds',
    'stopsUndoSnapshot',
    'stopsUndone',
    'hasRoutedStopsOnce',
    'hasStopsTableChangedOnce',
    'clusterType',
    'hasClustered',
    // Solução
    'routes',
    'markers',
    'lastRoutingSucceeded',
    'matchedStopIndexes',
    'computedDepartureTimes',
    'totalDuration',
    'routingTable',
    'stopsByRoute',
    'routingOriginalStopsSnapshot',
    'unallocatedStopsTableItems',
    'unallocatedGroupReasons',
    'walkLegsSettled',
    'visibleRouteIds',
    'routeTypeFilter',
    'highlightedRotaId',
    'routeStopsTab',
    'routeSummaryOptions',
    'fleetCostSummary',
    'fleetVehicleSpecById',
    'merusRouteBaselineById',
    'driverSchedule',
    'driverScheduleError',
    // Passageiros da unidade do destino incluídos automaticamente (modo operação)
    'autoAddedUnitPassengerIds',
    'autoAddedUnitId',
    // Histórico: cada destino grava a sua própria linha
    'historicoSessaoId',
    'historicoLoadedId',
    // Mapa
    'center',
    'zoom',
]);

/**
 * Propriedades de instância (fora do `data`) que acompanham a solução do destino. As polylines
 * Leaflet (`_routePolylines`) não entram: pertencem ao `<l-map>` montado e são recriadas a partir
 * do geojson das rotas quando o mapa remonta na troca de destino.
 */
export const DESTINATION_CONTEXT_INSTANCE_FIELDS = Object.freeze([
    '_preclusterBySchedule',
    '_pipelineWalkLegs',
    '_pipelineRotasAvisos',
    // O FINAL (caminhadas) de cada destino só aplica se o token dele ainda for o vivo.
    '_pipelineRunToken',
    '_pipelineFinalPromise',
    '_historicoIdUnidade',
]);

/**
 * Parâmetros que um destino novo herda do ativo. O resto (paradas, solução, turnos, ciclos de
 * motorista, tipos/contratos da unidade) nasce vazio e é buscado para o destino novo.
 */
export const DESTINATION_INHERITED_FIELDS = Object.freeze([
    'selectedDirection',
    'maxTripDuration',
    'maxNumberofStops',
    'isClusteringEnabled',
    'serviceTime',
    'stopServiceMode',
    'stopServiceFixedSec',
    'stopServicePerPassengerSec',
    'alightServiceMode',
    'alightServiceFixedSec',
    'alightServicePerPassengerSec',
    'radiusConfig',
    'centerRadius',
    'previewSchedule',
    'operationDays',
    'operationTaxes',
    'minSimilarity',
    'capacity',
    'tempoReusoVeiculos',
    'selectedFleetMode',
    'routingCompanyId',
    'routingClientId',
    'manualFleetEntries',
    'selectedAvailableVehicleType',
    'simulationFleetCosts',
    'driverParams',
    'center',
    'zoom',
]);

/** Valores iniciais dos campos que não são herdados (espelham o `data` do componente). */
function emptyContextDefaults() {
    return {
        destination: null,
        previewArea: { active: false, corners: [], polygon: null, bbox: null, geojson: null },
        initialBase: null,
        initialBaseStop: 0,
        operationalVehicleTypeRows: [],
        selectedOperationalVehicleTypes: [],
        operationalContractsRows: [],
        selectedContracts: [],
        operationalFleetPercentOverrides: {},
        destinationShifts: [],
        destinationShiftsFonte: null,
        driverCyclesSource: null,
        stops: [],
        originalCsvPoints: [],
        csvImportActive: false,
        csvImportPersonIds: [],
        stopsUndoSnapshot: null,
        stopsUndone: false,
        hasRoutedStopsOnce: false,
        hasStopsTableChangedOnce: false,
        clusterType: null,
        hasClustered: false,
        routes: [],
        markers: [],
        lastRoutingSucceeded: false,
        matchedStopIndexes: [],
        computedDepartureTimes: [],
        totalDuration: {},
        routingTable: [],
        stopsByRoute: [],
        routingOriginalStopsSnapshot: [],
        unallocatedStopsTableItems: [],
        unallocatedGroupReasons: {},
        walkLegsSettled: false,
        visibleRouteIds: [],
        routeTypeFilter: [],
        highlightedRotaId: null,
        routeStopsTab: 0,
        routeSummaryOptions: { page: 1, itemsPerPage: 10 },
        fleetCostSummary: null,
        fleetVehicleSpecById: {},
        merusRouteBaselineById: {},
        driverSchedule: null,
        driverScheduleError: '',
        autoAddedUnitPassengerIds: [],
        autoAddedUnitId: null,
        historicoSessaoId: null,
        historicoLoadedId: null,
    };
}

function emptyInstanceDefaults() {
    return {
        _preclusterBySchedule: null,
        _pipelineWalkLegs: null,
        _pipelineRotasAvisos: null,
        _pipelineRunToken: null,
        _pipelineFinalPromise: null,
        _historicoIdUnidade: null,
    };
}

function cloneJson(value) {
    if (value == null || typeof value !== 'object') return value;
    return JSON.parse(JSON.stringify(value));
}

/** Guarda (por referência) o estado do destino ativo. */
export function captureDestinationContext(vm) {
    const data = {};
    DESTINATION_CONTEXT_FIELDS.forEach((field) => {
        data[field] = vm[field];
    });
    const instance = {};
    DESTINATION_CONTEXT_INSTANCE_FIELDS.forEach((field) => {
        instance[field] = vm[field];
    });
    return { data, instance };
}

/** Aplica um contexto capturado nos campos vivos. Campos ausentes no contexto não são tocados. */
export function restoreDestinationContext(vm, ctx) {
    if (!ctx) return;
    const data = ctx.data || {};
    DESTINATION_CONTEXT_FIELDS.forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(data, field)) vm[field] = data[field];
    });
    const instance = ctx.instance || {};
    DESTINATION_CONTEXT_INSTANCE_FIELDS.forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(instance, field)) vm[field] = instance[field];
    });
}

/**
 * Contexto de um destino recém-adicionado: herda os parâmetros do ativo (cópia profunda, para
 * que editar um destino não altere o outro) e começa sem paradas nem solução.
 */
export function buildInheritedDestinationContext(vm, destination) {
    const data = emptyContextDefaults();
    DESTINATION_INHERITED_FIELDS.forEach((field) => {
        data[field] = cloneJson(vm[field]);
    });
    data.destination = destination ? { ...destination } : null;
    return { data, instance: emptyInstanceDefaults() };
}

/** Raio máximo (m) do destino: o raio de unidades do contexto, com o fallback do componente. */
export function destinationRadiusFromContext(data) {
    if (!data) return NaN;
    const fromConfig = Number(data.radiusConfig && data.radiusConfig.radiusUnitCenter);
    if (Number.isFinite(fromConfig) && fromConfig > 0) return fromConfig;
    const fallback = Number(data.centerRadius);
    return Number.isFinite(fallback) && fallback > 0 ? fallback : NaN;
}

function stopLatLng(stop) {
    if (!stop) return null;
    const lat = Number(stop.latitude != null ? stop.latitude : stop.lat);
    const lng = Number(stop.longitude != null ? stop.longitude : stop.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
}

/** Unidade de negócio definida para a parada (CSV `cod_unidade` ou passageiro cadastrado), ou null. */
export function stopFixedUnitId(stop) {
    if (!stop || stop.id_unidade_destino == null || stop.id_unidade_destino === '') return null;
    const id = Number(stop.id_unidade_destino);
    return Number.isFinite(id) && id > 0 ? id : null;
}

/** Cerca de destino definida para a parada (CSV `cod_cerca`, só na simulação), ou null. */
export function stopFixedFenceId(stop) {
    if (!stop || stop.id_cerca_destino == null || stop.id_cerca_destino === '') return null;
    const id = Number(stop.id_cerca_destino);
    return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Destino definido da parada conforme o modo: `{ type: 'fence' | 'unit', id }` ou null.
 * `fixedFence` (simulação) só vale com cerca na parada e prevalece sobre `fixedUnit`.
 */
export function stopFixedTarget(stop, options = {}) {
    if (options && options.fixedFence) {
        const fence = stopFixedFenceId(stop);
        if (fence != null) return { type: 'fence', id: fence };
    }
    if (options && options.fixedUnit) {
        const unit = stopFixedUnitId(stop);
        if (unit != null) return { type: 'unit', id: unit };
    }
    return null;
}

/** As duas paradas têm destino definido (conforme o modo) e são destinos diferentes. */
export function stopFixedTargetsConflict(a, b, options = {}) {
    const ta = stopFixedTarget(a, options);
    const tb = stopFixedTarget(b, options);
    return ta != null && tb != null && (ta.type !== tb.type || ta.id !== tb.id);
}

/** As duas paradas têm unidade definida e são unidades diferentes (não podem ser mescladas). */
export function stopFixedUnitsConflict(a, b) {
    const ua = stopFixedUnitId(a);
    const ub = stopFixedUnitId(b);
    return ua != null && ub != null && ua !== ub;
}

function withinRadius(dest, dist) {
    const raio = Number(dest.raioMax);
    return !(Number.isFinite(raio) && raio > 0) || dist <= raio;
}

/**
 * Atribui cada parada a um destino.
 *
 * @param {Object[]} stops paradas com `latitude`/`longitude` (ou `lat`/`lng`)
 * @param {{ key: string, lat: number, lng: number, nome?: string, raioMax?: number, idUnidade?: number, idCerca?: number }[]} destinations
 *   `raioMax` ausente/inválido = destino sem limite de raio
 * @param {{ fixedUnit?: boolean, fixedFence?: boolean }} [options] `fixedUnit`: parada com
 *   `id_unidade_destino` vai só para o destino daquela unidade (respeitando o raio dele);
 *   `fixedFence`: idem para `id_cerca_destino` e o destino daquela cerca (prevalece sobre a
 *   unidade); as sem destino definido seguem a regra da distância.
 * @returns {{
 *   byKey: Map<string, Object[]>,
 *   outside: Object[],
 *   outsideFixed: Object[],
 *   unitWithoutDestination: Object[],
 *   withoutCoords: Object[],
 * }} `outside` = fora do raio de todos (com `_destinoMaisProximo*` preenchidos);
 *   `outsideFixed` = fora do raio do destino da própria unidade (`_destinoMaisProximo*` = esse
 *   destino); `unitWithoutDestination` = unidade definida sem destino na tela;
 *   `withoutCoords` = sem coordenada válida. As paradas atribuídas recebem
 *   `_destinoKey`, `_destinoNome`, `_destinoDistM` e `_destinoPorUnidade`.
 */
export function assignStopsToDestinations(stops, destinations, options = {}) {
    const targetOptions = {
        fixedUnit: !!(options && options.fixedUnit),
        fixedFence: !!(options && options.fixedFence),
    };
    const byKey = new Map();
    const outside = [];
    const outsideFixed = [];
    const unitWithoutDestination = [];
    const withoutCoords = [];
    const dests = (Array.isArray(destinations) ? destinations : []).filter(
        (d) => d && d.key != null && Number.isFinite(Number(d.lat)) && Number.isFinite(Number(d.lng))
    );
    dests.forEach((d) => byKey.set(String(d.key), []));
    const destByUnit = new Map();
    const destByFence = new Map();
    dests.forEach((d) => {
        const unit = Number(d.idUnidade);
        if (Number.isFinite(unit) && unit > 0 && !destByUnit.has(unit)) destByUnit.set(unit, d);
        const fence = Number(d.idCerca);
        if (Number.isFinite(fence) && fence > 0 && !destByFence.has(fence)) destByFence.set(fence, d);
    });

    (Array.isArray(stops) ? stops : []).forEach((stop) => {
        if (!stop) return;
        const pos = stopLatLng(stop);
        if (!pos) {
            withoutCoords.push(stop);
            return;
        }
        const target = stopFixedTarget(stop, targetOptions);
        if (target != null) {
            const d = target.type === 'fence' ? destByFence.get(target.id) : destByUnit.get(target.id);
            stop._destinoKey = null;
            stop._destinoNome = null;
            stop._destinoDistM = null;
            if (!d) {
                delete stop._destinoMaisProximoNome;
                delete stop._destinoMaisProximoDistM;
                unitWithoutDestination.push(stop);
                return;
            }
            const dist = haversineMeters(pos.lat, pos.lng, d.lat, d.lng);
            if (!Number.isFinite(dist)) {
                withoutCoords.push(stop);
                return;
            }
            if (!withinRadius(d, dist)) {
                stop._destinoMaisProximoNome = d.nome || String(d.key);
                stop._destinoMaisProximoDistM = Math.round(dist);
                outsideFixed.push(stop);
                return;
            }
            stop._destinoKey = String(d.key);
            stop._destinoNome = d.nome || String(d.key);
            stop._destinoDistM = Math.round(dist);
            stop._destinoPorUnidade = true;
            delete stop._destinoMaisProximoNome;
            delete stop._destinoMaisProximoDistM;
            byKey.get(String(d.key)).push(stop);
            return;
        }
        delete stop._destinoPorUnidade;
        const ranked = dests
            .map((d) => ({ d, dist: haversineMeters(pos.lat, pos.lng, d.lat, d.lng) }))
            .filter((item) => Number.isFinite(item.dist))
            // Empate: mantém a ordem em que os destinos foram adicionados.
            .sort((a, b) => a.dist - b.dist);
        if (!ranked.length) {
            withoutCoords.push(stop);
            return;
        }
        const chosen = ranked.find(({ d, dist }) => withinRadius(d, dist));
        if (!chosen) {
            const nearest = ranked[0];
            stop._destinoKey = null;
            stop._destinoNome = null;
            stop._destinoDistM = null;
            stop._destinoMaisProximoNome = nearest.d.nome || String(nearest.d.key);
            stop._destinoMaisProximoDistM = Math.round(nearest.dist);
            outside.push(stop);
            return;
        }
        stop._destinoKey = String(chosen.d.key);
        stop._destinoNome = chosen.d.nome || String(chosen.d.key);
        stop._destinoDistM = Math.round(chosen.dist);
        delete stop._destinoMaisProximoNome;
        delete stop._destinoMaisProximoDistM;
        byKey.get(String(chosen.d.key)).push(stop);
    });

    return { byKey, outside, outsideFixed, unitWithoutDestination, withoutCoords };
}
